import React, {useRef, useState, useEffect, useCallback} from 'react';
import {Tree, Skeleton} from 'antd';
import type {FileItem, TreeNode, DirectorySource, DirectoryTreeProps} from '../../types/directory';
import {directoryAdapter} from '../../adapters/directory';
import {useDirectoryTree} from '../../hooks/useDirectoryTree';
import './index.scss';
import {TeamOutlined, ApartmentOutlined, StarOutlined} from '@ant-design/icons';
import {useCurrentUser} from '../../hooks/useCurrentUser';

export function DirectoryTree({
                                currentId,
                                rootId = 1,
                                onSelect,
                                mode = 'navigation',
                                onRootLoaded,
                                refreshKey,
                                refreshParentId,
                                source = 'directory',
                                onDefaultNodeLoaded,
                                expandedIds,
                                checkable,
                                checkStrictly,
                                checkedKeys,
                                onCheck,
                                disableNode
                              }: DirectoryTreeProps) {
  const {data: currentUser} = useCurrentUser();
  const {data, isLoading} = useDirectoryTree(rootId, source, currentUser?.user_id);

  const [expandedKeys, setExpandedKeys] = useState<number[]>([]);
  const [treeData, setTreeData] = useState<TreeNode[]>([]);
  const loadedKeys = useRef(new Set<number>());
  const onDefaultNodeLoadedRef = useRef(onDefaultNodeLoaded);

  const canLazyLoad = mode === 'picker';

  const buildNode = useCallback((
    item: FileItem,
    isLeaf: boolean,
    isRoot = false,
    level = 0,
    path?: string,
    parentSegments: Array<{
      id: number | string;
      name: string;
      path: string;
      is_private?: boolean;
    }> = []
  ): TreeNode => {
    const nodePath = path || item.full_path || item.name;

    const currentSegment = {
      id: item.id,
      name: item.name,
      path: nodePath,
      is_private: item.is_private
    };

    const node: TreeNode = {
      title: (
        <div className="drive-directory-tree__node">
          {isRoot
            ? <ApartmentOutlined className="drive-directory-tree__root-icon"/>
            : item.is_private
              ? <StarOutlined className="drive-directory-tree__folder-icon drive-directory-tree__my-file-icon"/>
              : <TeamOutlined className="drive-directory-tree__folder-icon"/>
          }

          <span className="drive-directory-tree__text">
            {item.name}
          </span>
        </div>
      ),
      rawTitle: item.name,
      key: item.id,
      path: nodePath,
      is_private: item.is_private,
      level,
      isLeaf,
      segments: [...parentSegments, currentSegment],
      className: isRoot
        ? 'drive-directory-tree--root-node'
        : item.is_private
          ? 'drive-directory-tree__my-file-node'
          : undefined
    };

    return {
      ...node,
      disabled: disableNode?.(node),
    };
  }, [disableNode]);

  const buildTreeNode = useCallback((
    item: FileItem,
    parentPath: string,
    level: number,
    parentSegments: Array<{
      id: number | string;
      name: string;
      path: string;
      is_private?: boolean;
    }>
  ): TreeNode => {
    const currentPath = item.full_path || `${parentPath}/${item.name}`;

    const node = buildNode(
      item,
      false,
      false,
      level,
      currentPath,
      parentSegments
    );

    const children = (item.children || [])
      .filter((child: any) => child.type === 'directory')
      .map((child: any) =>
        buildTreeNode(
          child,
          currentPath,
          level + 1,
          node.segments || [],
        )
      );

    return {
      ...node,
      children,
      isLeaf: canLazyLoad ? false : children.length === 0,
    };
  }, [buildNode, canLazyLoad]);

  const loadChildren = useCallback(async (node: TreeNode, force = false) => {
    const key = node.key;

    if (!force && loadedKeys.current.has(key)) return;

    loadedKeys.current.add(key);

    const nextSource: DirectorySource =
      source === 'mixed'
        ? (node.level ?? 0) >= 1
          ? 'directory'
          : 'navigation'
        : source;

    const res = await directoryAdapter.getChildren(key, nextSource, rootId);

    const nextChildren = res.children
      .filter((item: any) => item.type === 'directory' && !item.is_shared)
      .map((item: FileItem) => {
        const currentPath = item.full_path || `${node.path}/${item.name}`;

        return buildNode(
          item,
          !canLazyLoad,
          false,
          (node.level ?? 0) + 1,
          currentPath,
          node.segments || []
        );
      });

    setTreeData(prev => updateTreeData(prev, key, nextChildren));
  }, [buildNode, canLazyLoad, rootId, source]);

  useEffect(() => {
    if (!data) return;

    loadedKeys.current.clear();

    const rootNode = buildNode(data.currentDirectory as FileItem, false, true, 0);

    onRootLoaded?.({
      id: rootNode.key,
      name: rootNode.rawTitle,
      path: rootNode.path,
      is_private: rootNode.is_private
    });

    const children = data.children
      .filter((item: any) => {
        if (item.type !== 'directory') {
          return false;
        }

        return !(checkable && item.is_private);
      })
      .map((item: FileItem) =>
        buildTreeNode(
          item,
          rootNode.path,
          1,
          rootNode.segments || []
        )
      );

    const myFileNode = children.find((item: TreeNode) => item.is_private);

    if (source === 'navigation' && mode === 'navigation' && myFileNode) {
      onDefaultNodeLoadedRef.current?.({
        id: myFileNode.key,
        name: myFileNode.rawTitle,
        path: myFileNode.path,
        is_private: true
      });
    }

    setTreeData([
      {
        ...rootNode,
        children,
        isLeaf: children.length === 0
      }
    ]);

    loadedKeys.current.add(rootNode.key);

    // 递归收集所有节点 key（含子节点）
    function collectAllKeys(nodes: TreeNode[]): number[] {
      const keys: number[] = [];
      for (const node of nodes) {
        keys.push(Number(node.key));
        if (node.children?.length) {
          keys.push(...collectAllKeys(node.children));
        }
      }
      return keys;
    }

    // 页面初始化时自动展开所有节点
    setExpandedKeys(prev => {
      const allKeys = [Number(rootNode.key), ...collectAllKeys(children)];

      const existing = new Set(prev.map(k => Number(k)));
      const toAdd = allKeys.filter(k => !existing.has(k));

      if (!toAdd.length) return prev;

      return [...prev, ...toAdd];
    });
  }, [data, buildNode, onRootLoaded, mode, source, buildTreeNode, checkable]);

  useEffect(() => {
    if (!refreshKey || !refreshParentId) return;

    void loadChildren({
      title: '',
      rawTitle: '',
      key: Number(refreshParentId),
      path: '',
      level: 1,
      isLeaf: false,
      segments: []
    }, true);
  }, [refreshKey, refreshParentId, loadChildren]);

  useEffect(() => {
    onDefaultNodeLoadedRef.current = onDefaultNodeLoaded;
  }, [onDefaultNodeLoaded]);

  useEffect(() => {
    if (!expandedIds?.length) return;

    setExpandedKeys(prev => {
      const nextKeys = expandedIds.map(id => Number(id));

      return Array.from(new Set([
        ...prev,
        ...nextKeys
      ]));
    });
  }, [expandedIds]);

  async function loadData(node: any) {
    if (!canLazyLoad) return;

    await loadChildren({
      title: node.title,
      rawTitle: node.rawTitle,
      key: node.key,
      path: node.path,
      level: node.level,
      isLeaf: node.isLeaf,
      segments: node.segments
    });
  }

  async function handleSelect(_: any, info: any) {
    const node = info.node;
    const key = Number(node.key);

    if (mode === 'navigation' || mode === 'picker') {
      const isExpanded = expandedKeys.includes(key);
      const isRootNode = node.level === 0;

      if (!isExpanded) {
        setExpandedKeys(prev => {
          if (isRootNode) {
            return prev.includes(key) ? prev : [...prev, key];
          }

          return Array.from(new Set([...prev, key]));
        });

        if (canLazyLoad && !node.isLeaf && !loadedKeys.current.has(key)) {
          await loadData(node);
        }
      }
    }

    onSelect?.({
      id: node.key,
      name: node.rawTitle,
      path: node.path,
      level: node.level,
      is_private: node.is_private,
      segments: node.segments
    });
  }

  if (isLoading && !treeData.length) {
    return <div className="drive-directory-tree drive-directory-tree--loading">
      <Skeleton active paragraph={{rows: 6}}/>
    </div>;
  }

  return (
    <div className={`drive-directory-tree drive-directory-tree--${mode}`}>
      <Tree
        disabled={isLoading}
        treeData={treeData}
        loadData={canLazyLoad ? loadData : undefined}
        onSelect={handleSelect}
        onCheck={(keys, info) => {
          const nextKeys = Array.isArray(keys) ? keys : keys.checked;

          onCheck?.(
            nextKeys as Array<number | string>,
            info.checkedNodes
          );
        }}
        selectedKeys={currentId != null ? [Number(currentId)] : []}
        expandedKeys={expandedKeys}
        onExpand={(keys) => {
          const rootKey = treeData[0]?.key;

          if (rootKey === null) {
            setExpandedKeys(keys as number[]);
            return;
          }

          setExpandedKeys(Array.from(new Set([rootKey, ...(keys as number[])])));
        }}
        blockNode
        showIcon={false}
        showLine={false}
        switcherIcon={mode === 'navigation' ? null : undefined}
        motion={null}
        checkable={checkable}
        checkStrictly={checkStrictly}
        checkedKeys={checkedKeys}
      />
    </div>
  );
}

function updateTreeData(
  list: TreeNode[],
  key: number,
  children: TreeNode[]
): TreeNode[] {
  return list.map(node => {
    if (node.key === key) {
      return {
        ...node,
        children,
        isLeaf: children.length === 0
      };
    }

    if (node.children) {
      return {
        ...node,
        children: updateTreeData(node.children, key, children),
      };
    }

    return node;
  });
}
